import React, { useState, useEffect } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getCurrentLocation } from '@/utils/geolocation';
import { useTranslation } from 'react-i18next';

interface Hospital {
  id: string;
  name: string;
  address: string;
  distance: number;
  latitude: number;
  longitude: number;
  mapsUrl: string;
  specialized_in_anemia: boolean;
}

// Función para calcular la distancia entre dos puntos geográficos (fórmula de Haversine)
const calculateDistance = (
  lat1: number, 
  lon1: number, 
  lat2: number, 
  lon2: number
): number => {
  const R = 6371; // Radio de la Tierra en km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c; // Distancia en km
  return distance;
};

// Función para generar URL de Google Maps
const getMapsUrl = (name: string, lat: number, lng: number): string => {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name)}&query_place_id=${lat},${lng}`;
};

const HospitalsTab: React.FC = () => {
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [userLocation, setUserLocation] = useState<{latitude: number | null, longitude: number | null}>({
    latitude: null,
    longitude: null
  });
  const { t } = useTranslation();

  // Cargar la ubicación del usuario
  useEffect(() => {
    const loadUserLocation = async () => {
      try {
        const location = await getCurrentLocation();
        setUserLocation({
          latitude: location.latitude,
          longitude: location.longitude
        });
      } catch (error) {
        console.error("Error getting location:", error);
        toast.error("No se pudo obtener tu ubicación");
      }
    };
    
    loadUserLocation();
  }, []);
  
  // Cargar hospitales de la base de datos
  useEffect(() => {
    const fetchHospitals = async () => {
      try {
        setLoading(true);
        
        // Using type assertion to bypass TypeScript validation
        const { data: dbHospitals, error } = await supabase
          .from('hospitals')
          .select('*')
          .returns<any[]>();
          
        if (error) {
          throw error;
        }
        
        if (dbHospitals && dbHospitals.length > 0) {
          // Procesar hospitales de la base de datos
          const processedHospitals: Hospital[] = dbHospitals.map((hospital: any) => {
            const distance = userLocation.latitude && userLocation.longitude ? 
              calculateDistance(
                userLocation.latitude, 
                userLocation.longitude, 
                hospital.latitude, 
                hospital.longitude
              ) : 999;
              
            return {
              id: hospital.id,
              name: hospital.name,
              address: hospital.address || '',
              distance: Number(distance.toFixed(1)),
              latitude: hospital.latitude,
              longitude: hospital.longitude,
              mapsUrl: getMapsUrl(hospital.name, hospital.latitude, hospital.longitude),
              specialized_in_anemia: hospital.specialized_in_anemia || false
            };
          });
          
          // Ordenar por distancia
          processedHospitals.sort((a, b) => a.distance - b.distance);
          
          setHospitals(processedHospitals);
        } else {
          // Si no hay hospitales en la base de datos, usar los de respaldo
          const backupHospitals: Hospital[] = [
            {
              id: '1',
              name: 'Hospital Nacional Cayetano Heredia',
              address: 'Av. Honorio Delgado 262, San Martín de Porres',
              distance: 1.2,
              latitude: -12.0245, 
              longitude: -77.0577,
              mapsUrl: 'https://maps.app.goo.gl/YvwkHe5xdq3GCe6t9',
              specialized_in_anemia: true
            },
            {
              id: '2',
              name: 'Centro de Salud Los Olivos',
              address: 'Jr. Huaraz 1765, Los Olivos',
              distance: 2.5,
              latitude: -11.9769, 
              longitude: -77.0678,
              mapsUrl: 'https://maps.app.goo.gl/DTnVhJ5YbZnfHmQZ6',
              specialized_in_anemia: false
            },
            {
              id: '3',
              name: 'Clínica San Pablo',
              address: 'Av. El Polo 789, Surco',
              distance: 5.8,
              latitude: -12.1092, 
              longitude: -76.9702,
              mapsUrl: 'https://maps.app.goo.gl/fD4mBgGr3yL7mdyu9',
              specialized_in_anemia: true
            },
          ];
          setHospitals(backupHospitals);
        }
      } catch (error) {
        console.error("Error fetching hospitals:", error);
        toast.error("Error al cargar los hospitales");
      } finally {
        setLoading(false);
      }
    };
    
    fetchHospitals();
  }, [userLocation]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="w-6 h-6 text-sangre-500 animate-spin" />
        <span className="ml-2">{t('loadingHealthCenters')}</span>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <h3 className="text-lg font-medium text-gray-800 mb-3">{t('nearbyHealthCenters')}</h3>
      <div className="space-y-4">
        {hospitals.map(hospital => (
          <div key={hospital.id} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
            <div>
              <h4 className="font-medium text-gray-800 text-md mb-1">{hospital.name}</h4>
              <p className="text-sm text-gray-600 mb-2">{hospital.address}</p>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs bg-gray-100 px-2 py-1 rounded-full text-gray-700">{hospital.distance} km</span>
              <a 
                href={hospital.mapsUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm font-medium text-fuerte-600 hover:text-fuerte-700"
              >
                {t('howToGet')} <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        ))}
        
        {hospitals.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>{t('noHealthCentersAvailable')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HospitalsTab;
